import React, {useMemo, useCallback, useContext, useEffect, useState} from 'react';
import classnames from 'classnames';
import {Map, Set, List} from 'immutable';
import {pluralize} from 'lib/stringUtils';
import {
  LoadingSpinner,
  Checkbox,
  IconButton,
  Text,
  WithTooltip,
  Expandable,
  WithToggle
} from '@albert-io/atomic';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';

import {StandardSetObjectType, StandardSet, Standard} from './StandardsModal.types';

import {StandardSetModelV1} from 'resources/augmented/StandardSet/StandardSetModel.v1';
import {StandardModelV1} from 'resources/augmented/Standard/StandardModel.v1';

import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';

import sessionStore from 'client/Session/SessionStore';

import './standards-results.scss';
import {useTracking} from 'react-tracking';
import {
  getStandardSetStandardsQuestionCountQuery,
  getStandardSetsQuestionCountQuery
} from '../../Document/DocumentContentDiscovery/FilterGroup/FilterGroup.queries';

interface StandardSetCounts {
  [standardSetId: string]: number;
}

interface ResultsProps {
  isLoading?: boolean;
  showMatches?: boolean;
  formattedResults: Map<string, StandardSetObjectType>;
  setFormattedResults: (formattedRedults) => void;
  unsavedStandardCollection: Map<string, StandardSetObjectType>;
  setUnsavedStandardCollection: (formattedRedults) => void;
  filteredStandardSets: Set<StandardSet>;
  updateMapValue: any;
}

const StandardsResults = ({
  updateMapValue,
  isLoading,
  showMatches,
  formattedResults,
  filteredStandardSets,
  setFormattedResults,
  unsavedStandardCollection,
  setUnsavedStandardCollection
}: ResultsProps) => {
  const [standardSetCounts, setStandardSetCounts] = useState({} as StandardSetCounts);
  const [countsLoaded, setCountsLoaded] = useState(false);

  const {
    selectedGuideLevels,
    questionDifficulties,
    questionTypes,
    hideAssessmentQuestions,
    hidePreviouslyAssigned
  } = useContext(ContentDiscoveryContext);

  const standardSetClickHandler = (standardSetId) => {
    let updatedStandardSetObj = formattedResults?.get(standardSetId);
    if (!updatedStandardSetObj) return;
    const toggleVal = !updatedStandardSetObj.get('isSelected');
    const standardsToToggle = updatedStandardSetObj.get('standards') as any;

    // standards within the set
    updatedStandardSetObj = updatedStandardSetObj.updateIn(['standards'], (standardsMap) =>
      standardsMap.map((standard) => standard.set('isSelected', toggleVal))
    );
    // standard set
    // @ts-ignore  - TODO: fix this
    updatedStandardSetObj = updatedStandardSetObj.set('isSelected', toggleVal);
    // @ts-ignore  - TODO: fix this
    updatedStandardSetObj = updatedStandardSetObj.set('isIndeterminate', false);
    // update the overall standardSet
    setFormattedResults(updateMapValue(formattedResults, [standardSetId], updatedStandardSetObj));
    let updatedUnsavedCollection = unsavedStandardCollection?.get(standardSetId);

    if (showMatches) {
      // Toggle selected items but not parent if filtered down
      standardsToToggle.toArray().forEach((standardObj) => {
        const standard = standardObj.get('standard') as Standard;
        updatedUnsavedCollection = updateOrAddStandard(
          ['standards', standard.getId()],
          toggleVal,
          standard,
          updatedUnsavedCollection
        );
      });

      const allStandards = updatedUnsavedCollection.get('standards') as any;

      if (!toggleVal) {
        // if deselecting
        // if all items are deselected
        if (standardsToToggle.size === allStandards.size) {
          // @ts-ignore  - TODO: fix this
          updatedUnsavedCollection = updatedUnsavedCollection.set('isSelected', toggleVal);
          // @ts-ignore  - TODO: fix this
          updatedUnsavedCollection = updatedUnsavedCollection.set('isIndeterminate', false);
        } else {
          // else set to indeterminate because only a subset of standards are deselected
          // @ts-ignore  - TODO: fix this
          updatedUnsavedCollection = updatedUnsavedCollection.set('isIndeterminate', true);
        }
      }
      if (toggleVal) {
        // if selecting
        // if all items are selected
        if (standardsToToggle.size === allStandards.size) {
          // @ts-ignore  - TODO: fix this
          updatedUnsavedCollection = updatedUnsavedCollection.set('isSelected', false);
          // @ts-ignore  - TODO: fix this
          updatedUnsavedCollection = updatedUnsavedCollection.set('isIndeterminate', true);
        } else {
          // else set to indeterminate because only a subset of standards are selected
          // @ts-ignore  - TODO: fix this
          updatedUnsavedCollection = updatedUnsavedCollection.set('isIndeterminate', true);
        }
      }

      setUnsavedStandardCollection(
        updateMapValue(unsavedStandardCollection, [standardSetId], updatedUnsavedCollection)
      );
    } else {
      // toggle entire standards collection
      updatedUnsavedCollection = updatedUnsavedCollection.updateIn(['standards'], (standardsMap) =>
        standardsMap.map((standard) => standard.set('isSelected', toggleVal))
      );
      // @ts-ignore  - TODO: fix this
      updatedUnsavedCollection = updatedUnsavedCollection.set('isSelected', toggleVal);
      // @ts-ignore  - TODO: fix this
      updatedUnsavedCollection = updatedUnsavedCollection.set('isIndeterminate', false);
      setUnsavedStandardCollection(
        updateMapValue(unsavedStandardCollection, [standardSetId], updatedUnsavedCollection)
      );
    }
  };

  const checkIndeterminate = useCallback(
    (resultsObj, standardSetId, standardsObj) => {
      // handle updating all standard sets
      const standardsCollection = standardsObj?.get(standardSetId).get('standards');
      if (standardsCollection) {
        const numSelected = standardsCollection.filter((standard) =>
          standard.get('isSelected')
        ).size;
        const numTotal = standardsCollection.size;
        const isIndeterminate = numSelected > 0 && numSelected < numTotal;
        let updatedUnsavedCollection = updateMapValue(
          standardsObj,
          [standardSetId, 'isIndeterminate'],
          isIndeterminate
        );
        if (isIndeterminate && standardsObj.get('isSelected')) {
          updatedUnsavedCollection = updateMapValue(
            standardsObj,
            [standardSetId, 'isSelected'],
            false
          );
        }
        // update to indeterminate if filtered down
        if (showMatches && numSelected > 0) {
          updatedUnsavedCollection = updateMapValue(
            standardsObj,
            [standardSetId, 'isIndeterminate'],
            true
          );
        }

        setUnsavedStandardCollection(updatedUnsavedCollection);
      }

      // handle formatted results
      const standards = resultsObj?.get(standardSetId).get('standards');
      if (!standards) return;
      const numSelected = standards.filter((standard) => standard.get('isSelected')).size;
      const numTotal = standards.size;
      const isIndeterminate = numSelected > 0 && numSelected < numTotal;
      let updatedResults = updateMapValue(
        resultsObj,
        [standardSetId, 'isIndeterminate'],
        isIndeterminate
      );

      if (isIndeterminate && resultsObj.get('isSelected')) {
        updatedResults = updateMapValue(resultsObj, [standardSetId, 'isSelected'], false);
      }
      // update to indeterminate if filtered down
      if (showMatches && numSelected > 0) {
        updatedResults = updateMapValue(resultsObj, [standardSetId, 'isIndeterminate'], true);
      }
      setFormattedResults(updatedResults);
    },
    [setFormattedResults, updateMapValue, setUnsavedStandardCollection, showMatches]
  );

  const standardClickHandler = useCallback(
    (standardId, standardSetId, val) => {
      const updatedFormattedResults = updateMapValue(
        formattedResults,
        [standardSetId, 'standards', standardId, 'isSelected'],
        val
      );

      const standardIdPath = [standardSetId, 'standards', standardId];
      const standardObj = formattedResults.getIn(standardIdPath);
      const updatedStandardsCollection = updateOrAddStandard(
        standardIdPath,
        val,
        standardObj.get('standard'),
        unsavedStandardCollection
      );
      checkIndeterminate(updatedFormattedResults, standardSetId, updatedStandardsCollection);
    },
    [checkIndeterminate, formattedResults, updateMapValue, unsavedStandardCollection]
  );

  useEffect(() => {
    const updateStandardSetQuestionCounts = async () => {
      if (!sessionStore.isSuper()) {
        return;
      }

      setCountsLoaded(false);
      const standardSetIds = formattedResults.keySeq().toArray().join();

      const standardSetsData: List<StandardSetModelV1> = await getStandardSetsQuestionCountQuery(
        standardSetIds,
        questionDifficulties,
        questionTypes,
        hideAssessmentQuestions,
        hidePreviouslyAssigned,
        selectedGuideLevels,
        ''
      ).getResourcePromise();

      const updatedCounts = {} as StandardSetCounts;
      standardSetsData.forEach((standardSetData) => {
        updatedCounts[standardSetData.getId()] =
          standardSetData.getMeta().getContentDiscoveryQuestionCount() || 0;
      });

      setStandardSetCounts(updatedCounts);
      setCountsLoaded(true);
    };

    if (formattedResults && !formattedResults.isEmpty()) {
      updateStandardSetQuestionCounts();
    }
  }, [formattedResults]);

  if (isLoading) {
    return (
      <div className='cd-modal__results-container'>
        <div className='cd-modal__loading-container u-pad_1'>
          <LoadingSpinner size={2} />
        </div>
      </div>
    );
  }

  if (!formattedResults || formattedResults.isEmpty()) {
    return (
      <div className='cd-modal__results-container'>
        {isLoading && (
          <div className='cd-modal__loading-container u-pad_1'>
            <LoadingSpinner size={2} />
          </div>
        )}
        {!isLoading && (
          <div className='cd-modal__loading-container'>
            <Text emphasis='lowest'>No results found</Text>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='cd-modal__results-container'>
      {isLoading && (
        <div className='cd-modal__loading-container u-pad_1'>
          <LoadingSpinner size={2} />
        </div>
      )}
      {formattedResults
        ?.toArray()
        .sort((resultA, resultB) => {
          if (!resultA && !resultB) return 0;
          if (!resultA) return -1;
          if (!resultB) return 1;
          const standardSetA = resultA.get('standardSet') as StandardSet;
          const standardSetB = resultB.get('standardSet') as StandardSet;
          if (standardSetA.getTitle() < standardSetB.getTitle()) {
            return -1;
          }
          if (standardSetA.getTitle() > standardSetB.getTitle()) {
            return 1;
          }
          return 0;
        })
        .map((result) => {
          if (!result) return null;
          const standardSet = result.get('standardSet') as StandardSet;
          const standardSetQuestionCount = standardSetCounts[standardSet.getId()];
          if (
            // Hide if filtering by standard sets and result is not included
            filteredStandardSets.size > 0 &&
            !filteredStandardSets
              .toArray()
              .some((filteredStandard) => filteredStandard.getId() === standardSet?.getId())
          ) {
            return null;
          }
          return (
            <>
              <NewExpandableResult
                standardSetObj={result}
                standardSetQuestionCount={standardSetQuestionCount}
                standardSetClickHandler={standardSetClickHandler}
                showMatches={showMatches}
                standardClickHandler={standardClickHandler}
                countsLoaded={countsLoaded}
              />
            </>
          );
        })}
    </div>
  );
};

const updateOrAddStandard = (standardIdPath, val, standard, standardsCollection) => {
  let updatedStandardsCollection = standardsCollection;

  if (standardsCollection.hasIn(standardIdPath)) {
    updatedStandardsCollection = standardsCollection.setIn([...standardIdPath, 'isSelected'], val);
  } else {
    const standardObj = Map({
      standard,
      isSelected: val
    });
    updatedStandardsCollection = standardsCollection.setIn(standardIdPath, standardObj);
  }

  return updatedStandardsCollection;
};

export default StandardsResults;

interface StandardCounts {
  [standardId: string]: number;
}

interface NewExpandableResultProps {
  standardSetObj: any;
  standardSetQuestionCount: number;
  standardSetClickHandler: any;
  standardClickHandler: any;
  showMatches: any;
  countsLoaded: boolean;
}

const NewExpandableResult = ({
  standardSetObj,
  standardSetQuestionCount,
  standardSetClickHandler,
  standardClickHandler,
  showMatches,
  countsLoaded
}: NewExpandableResultProps) => {
  const [standardCounts, setStandardCounts] = useState({} as StandardCounts);
  const [hasPreviouslyExpanded, setHasPreviouslyExpanded] = useState(false);
  const [standardCountsLoaded, setStandardCountsLoaded] = useState(false);

  const {
    selectedGuideLevels,
    questionDifficulties,
    questionTypes,
    hideAssessmentQuestions,
    hidePreviouslyAssigned
  } = useContext(ContentDiscoveryContext);

  const fadeText = sessionStore.isSuper() && countsLoaded && !standardSetQuestionCount;

  const standardSet = standardSetObj.get('standardSet');
  const standards = standardSetObj.get('standards');

  const expandableId = useMemo(() => `expandable-area-${standardSet.getId()}`, [standardSet]); // id of expandable div for querySelector reference to select children checkboxes
  const buttonId = useMemo(() => `expandable-button-${standardSet.getId()}`, [standardSet]); // id for aria-labelledby on Expandable
  const {trackEvent} = useTracking({type: 'checkbox', event: 'click'});

  const filteredIndeterminate = useMemo(() => {
    const numSelected = standards.filter((standard) => standard.get('isSelected')).size;
    return numSelected > 0 && showMatches;
  }, [standards, showMatches]);

  const loadStandardsQuestionCounts = async (standardSetId: string) => {
    if (!sessionStore.isSuper()) {
      return;
    }

    setStandardCountsLoaded(false);
    const standardSetData: StandardSetModelV1 = await getStandardSetStandardsQuestionCountQuery(
      standardSetId,
      questionDifficulties,
      questionTypes,
      hideAssessmentQuestions,
      hidePreviouslyAssigned,
      selectedGuideLevels,
      ''
    ).getResourcePromise();

    const standardsData: List<StandardModelV1> = standardSetData.getStandards();

    const updatedCounts = {} as StandardCounts;

    standardsData.forEach((standard) => {
      updatedCounts[standard.getId()] = standard.getMeta().getContentDiscoveryQuestionCount() || 0;
    });

    setStandardCounts(updatedCounts);
    setStandardCountsLoaded(true);
  };

  return (
    <WithToggle dismissOnBlur={false} defaultOnState={standardSetObj.get('isIndeterminate')}>
      {({onClick, on}) => (
        <div className='standards-modal__expandable-search-result'>
          {/* eslint-disable-next-line */}
          <div role='button' className='cd-modal__result-item' id={buttonId} onClick={onClick}>
            <IconButton
              size='s'
              icon={on ? 'chevron-down' : 'chevron-right'}
              label={on ? 'Collapse' : 'Expand'}
              className='cd-modal__result-description-tooltip'
            />

            <span className='u-pad-l_1 u-display_flex'>
              <Checkbox
                checked={standardSetObj.get('isSelected')}
                indeterminate={standardSetObj.get('isIndeterminate') || filteredIndeterminate}
                label='check this'
                color='purple'
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  standardSetClickHandler(standardSet.getId());
                  trackEvent({content: standardSet.getTitle()});
                  e.stopPropagation();
                }}
                className='u-pad-r_1'
                size='s'
              />
              <Text emphasis={fadeText ? 'lowest' : undefined}>
                <MarkdownRendererV2 text={standardSet.getTitle()} />
              </Text>
            </span>

            <span className='u-mar-l_auto'>
              {showMatches && (
                <span
                  className={classnames({
                    'cd-modal__total-results-matches': standards.size > 0
                  })}
                >
                  {standards.size} {pluralize('match', standards.size, 'matches')}
                </span>
              )}
              {
                /*
                  TODO: Enable for all users once feature is complete
                  See https://github.com/albert-io/nexus/issues/7059
                */
                sessionStore.isSuper() && countsLoaded && (
                  <Text size='s' className='u-mar-l_auto'>{`(${standardSetQuestionCount})`}</Text>
                )
              }
              {!!standardSet.getDescription() && (
                <WithTooltip
                  content={<MarkdownRendererV2 text={standardSet.getDescription()} />}
                  theme='light'
                  placement='left'
                >
                  <IconButton
                    size='s'
                    icon={['far', 'info-circle']}
                    className='cd-modal__result-description-tooltip'
                    label='View description'
                  />
                </WithTooltip>
              )}
            </span>
          </div>
          <Expandable aria-labelledby={buttonId} expanded={on} id={expandableId}>
            {(() => {
              if (on && !hasPreviouslyExpanded) {
                loadStandardsQuestionCounts(standardSet.getId());
                setHasPreviouslyExpanded(true);
              }
            })()}

            {standards
              .sort((standardObjA, standardObjB) => {
                const standardA = standardObjA.get('standard');
                const standardB = standardObjB.get('standard');
                if (standardA.getTitle() < standardB.getTitle()) {
                  return -1;
                }
                if (standardA.getTitle() > standardB.getTitle()) {
                  return 1;
                }
                return 0;
              })
              .map((standardObj) => {
                const standard = standardObj.get('standard');
                return (
                  <MResultItem
                    key={standard.getId()}
                    id={standard.getId()}
                    onClick={() => {
                      standardClickHandler(
                        standard.getId(),
                        standardSet.getId(),
                        !standardObj.get('isSelected')
                      );
                      trackEvent({content: standard.getTitle()});
                    }}
                    isMatching={showMatches}
                    info={{
                      name: standard.getTitle(),
                      description: standard.getDescription(),
                      questionCount: standardCounts[standard.getId()] || 0,
                      checked: standardObj.get('isSelected')
                    }}
                    standardCountsLoaded={standardCountsLoaded}
                  />
                );
              })
              .toArray()}
          </Expandable>
        </div>
      )}
    </WithToggle>
  );
};

interface ResultItemProps {
  id: string;
  info: {
    name: string;
    description: string;
    questionCount: string | number;
    checked: boolean;
  };
  onClick?: any;
  isMatching?: boolean;
  standardCountsLoaded: boolean;
}

const ResultItem = ({
  id,
  info,
  onClick,
  isMatching = false,
  standardCountsLoaded
}: ResultItemProps) => {
  const checkboxId = `cd-checkbox-${id}`;
  const resultId = `cd-result-${id}`;

  const fadeText = sessionStore.isSuper() && standardCountsLoaded && !info.questionCount;

  return (
    <label
      htmlFor={checkboxId}
      key={resultId}
      className={classnames('cd-modal__standards-result-item u-mar-l_10', {
        'cd-modal__results-matches': isMatching
      })}
    >
      <Checkbox
        size='s'
        label='check this'
        color='purple'
        onChange={(e) => onClick(e)}
        id={checkboxId}
        checked={info.checked}
      />
      <Text emphasis={fadeText ? 'lowest' : undefined} className='u-mar-l_1'>
        <MarkdownRendererV2 text={info.name} />
      </Text>
      {info.description && (
        <Text
          size='s'
          emphasis={fadeText ? 'lowest' : 'medium'}
          className='cd-modal__standards-result-description'
        >
          <MarkdownRendererV2 text={info.description} />
        </Text>
      )}
      {
        /*
          TODO: Enable for all users once feature is complete
          See https://github.com/albert-io/nexus/issues/7059
        */
        sessionStore.isSuper() && standardCountsLoaded && (
          <Text size='s' emphasis={info.questionCount ? undefined : 'lowest'}>
            {`(${info.questionCount})`}
          </Text>
        )
      }
    </label>
  );
};

const MResultItem = React.memo(ResultItem);
